<template>
  <div class="bg-gray-10 px-4">
    <section class="mx-auto max-w-[732px]">
      <h1 class="text-t1" v-html="webapp_config.msg.errorreport_commercial_heading"></h1>
      <p class="mb-6 text-preamble" v-html="webapp_config.msg.errorreport_commercial_text">
      </p>
    </section>
    <div class="max-w-[732px] mx-auto bg-white rounded-[15px] py-4 px-4 md:py-16 md:px-14 border border-solid border-gray-light">
      <template v-if="referenceNumber">
        <div>
          <h4 class="text-t4 mb-2">Tack för att du felanmäler via vår hemsida. </h4>
          <p class="mb-4">Ett ärende med ärendenummer <span class="font-thick">{{ referenceNumber }}</span> har skapats</p>
            <Button variant="primary" size="large" @click="reset()">
              Skapa en till felanmälan
            </Button>
        </div>
      </template>
      <template v-else>
        <Form @submit="onSubmit" :validation-schema="schema" v-autofill-event v-slot="{ meta }">
          <!--Actual form-->
          <fieldset>
            <div>
              <h2 class="text-t6 mb-[10px]">Företag eller organisation</h2>

              <p :class="{ control: true }" style="position: relative">
                <SearchField
                  :options="options"
                  placeholder="Sök efter adress eller företagsnamn"
                  :place-button-start="true"
                  :require-enter-to-search="false"
                  @update:modelValue="handleInputChange"
                  :display-label="false"
                  ref="searchInput"
                  :autoComplete="false"
                  type="search"
                  class="search"
                  :showLoader="isFetching"
                  :value="query"
                />


                <ul v-if="displayDropdown" class="dropdown" :style="{ top: inputRefHeight}">
                  <li v-if="!isFetching && Object.keys(options).length === 0" class="dropdown-item">
                    <Button variant="text"
                            class="font-proxima-nova dark-turquoise text-wrap text-start py-4 font-normal" disabled>Inga
                      träffar
                    </Button>
                  </li>
                  <li
                    tabindex="0"
                    v-for="{id, name} in options"
                    v-bind:key="id"
                    @focus="scrollTo"
                    class="dropdown-item"
                  >
                    <Button variant="text"
                            class="font-proxima-nova dark-turquoise text-wrap text-start py-4 font-normal"
                            @click="onChange(id, name)">{{ name }}
                    </Button>
                  </li>
                </ul>
              </p>
            </div>

            <div>
              <p>
                <Field
                  v-slot="{ field, meta }"
                  v-model="report.rentalObjectCode"
                  name="errorReportRentalObject"
                  type="text"
                  id="report-rental-object-input"
                >
                  <input
                    :class="inputClasses"
                    v-bind="field"
                    :data-invalid="!meta.valid && meta.touched ? 'true' : 'false'"
                    placeholder="Objektsnummer"
                    autocomplete="off"
                    disabled
                  />
                </Field>
              </p>
            </div>
          </fieldset>
          <fieldset>
            <div>
              <h2 class="text-t6 mb-[10px]">Kontaktuppgifter</h2>
              <p :class="{ control: true }">
                <Field
                  v-slot="{ field, meta }"
                  v-model="report.contact"
                  id="error-report-contact-person-input"
                  name="errorReportContactPerson"
                  type="text"
                >
                  <Input
                    id="contact"
                    type="text"
                    :class="inputClasses"
                    v-bind="field"
                    :data-invalid="!meta.valid && meta.touched ? 'true' : 'false'"
                    label="Kontaktperson"
                    floating-label=true
                  />
                </Field>
              </p>
            </div>
            <div>
                <Field
                  v-slot="{ field, meta }"
                  v-model="report.phone"
                  name="errorReportPhone"
                  type="text"
                  id="error-report-phone-input"
                >
                  <Input
                    id="phone"
                    :class="inputClasses"
                    v-bind="field"
                    :data-invalid="!meta.valid && meta.touched ? 'true' : 'false'"
                    label="Telefon"
                    floating-label
                  />
                </Field>
            </div>

          </fieldset>
          <fieldset>
          <div>
            <h2 class="text-t6 mb-[10px]">Vad är felet?</h2>
            <p :class="{ control: true }">
              <Field
                v-slot="{ field, meta }"
                v-model="report.description"
                name="errorReportDescription"
                type="text"
                id="error-report-description-input"
              >
                <textarea
                  id="description"
                  :class="inputClasses"
                  v-bind="field"
                  :data-invalid="!meta.valid && meta.touched ? 'true' : 'false'"
                  placeholder="Beskriv felet"
                  autocomplete="off"
                  rows="4"
                />
              </Field>
            </p>
          </div>
          </fieldset>
          <div>
            <p :class="{ control: true }">
              <CommercialErrorReportImage
                name="errorReportAttachments"
                type="file"
                accept="image/x-png, image/gif, image/jpeg"
                :compression="[
                  {
                    compression: 0.01,
                    maxHeight: 1000,
                    maxWidth: 1000,
                    toBase64: true,
                    mimeType: 'image/jpeg',
                  },
                ]"
                :toBase64="true"
                :onFileInput="addAttachmentsToReport"
                :onFileRemoved="removeAttachmentFromReport"
                :modelValue="report.images"
              />
            </p>
          </div>

          <Captcha @change="showSuccess = $event" name="" />

          <!---->
          <div class="mt-6 button-container">
            <Button type="submit" variant="primary" :loading="showLoader" :disabled="showLoader || !meta.valid || !showSuccess" loadingText="Skickar.." size="extralarge">
              Skicka
            </Button>
          </div>
        </Form>
      </template>
    </div>
  </div>
</template>

<script setup>

const schema = {
  errorReportRentalObject: 'required|max:50',
  errorReportContactPerson: 'required|max:50',
  errorReportPhone: 'required|min:7|regex:^([0 +][0-9 +-]+)$|',
  errorReportDescription: 'required|max:300',
};
</script>

<script>
import { mapGetters } from 'vuex';
import { Form, Field, ErrorMessage } from 'vee-validate';
import CommercialErrorReportImage from "@/pages/commercial-error-report-image.vue";
import Loader from '@/components/ui/loader';
import Captcha from '@/components/ui/captcha';
import Button from '@/components/ui/button';
import SearchField  from "../components/search/filters_new/searchfield.vue";
import Input from '@/components/ui/form/input';
import {debounce} from 'lodash';
export default {
  name: 'CommercialErrorReport',
  components: { SearchField, CommercialErrorReportImage, Loader, Captcha, Button, ErrorMessage, Field, Form, Input},
  data() {
    return {
      showLoader: false,
      referenceNumber: null,
      showSuccess: false,
      honeyTrap: {
        name: null,
        phone: null,
      },
      report: {
        rentalObjectCode: null,
        contact: '',
        phone: '',
        description: '',
        //confirmEmail : true,
        images: [],
        leaseId: null,
      },
      query: '',
      options: {},
      inputRef: null,
      inputRefHeight: '0px',
      displayDropdown: false,
      isFetching: false,
      inputClasses: 'bg-gray-light rounded-[6px] border-[1px] border-text-black placeholder-shown:text-gray-dark',
    };
  },
  computed: {
    ...mapGetters({ commercialPremises: 'commercialPremises', webapp_config: 'webapp_config', contracts: 'contracts' }),
  },
  mounted() {
    this.inputRef = this.$refs.searchInput;
    const inputRefEl = this.inputRef?.$el;
    if (inputRefEl) {
      const inputElement = inputRefEl.querySelector('input');
      const inputMargin = parseFloat(getComputedStyle(inputElement).marginBottom) || 0;
      this.inputRefHeight = `${inputRefEl.offsetHeight - inputMargin}px`;

      inputElement.addEventListener("blur", debounce(() => {
        this.displayDropdown = false;
      }, 200));

      inputElement.addEventListener("focus", debounce(() => {
        this.displayDropdown = true;
      }, 200));
    }
  },
  methods: {
    removeAttachmentFromReport: function (index) {
      const attachments = this.report.images;
      attachments.splice(index, 1);
      this.report.images = attachments;
      console.log(attachments)
      console.log("this.report.images", this.report.images)
    },
    addAttachmentsToReport(attachments) {
      if (!attachments.length) return;

      this.report.images = attachments.map((att, index) => {
        return {
          filename: "attachment-" + Date.now() + "-" + index,
          data: att.data,
        };
      });
    },



    async updateOptions() {
      this.isFetching = true;
      this.options = await this.$store.cache.dispatch('getCommercialPremises', {query: this.query});
      this.isFetching = false;
      this.displayDropdown = true;
    },

    async onChange(evt, name) {
      this.query = name;
      const premise = await this.$store.dispatch('getCommercialPremiseById', evt);
      this.report.rentalObjectCode = premise.premiseCode;
      this.report.leaseId = premise.leaseId;
      if(!!premise.contactName) {
        this.report.contact = premise.contactName;
      }

      if(!!premise.phone) {
        this.report.phone = premise.phone
      }

      this.displayDropdown = false;
    },

    onSubmit() {
      // return this.$validator.validateAll().then(result => {
      //if (result) {

      this.showLoader = true;
      return this.$store
        .dispatch('createCommercialErrorReport', this.report)
        .then((helpdeskid) => {
          this.referenceNumber = helpdeskid;
          window.scroll(0, 0);
        })
        .finally(() => {
          this.showLoader = false;
        });
      //}
      // });
    },

    handleInputChange: debounce(async function (value) {
      this.query = value;
      await this.updateOptions()
    }, 300),
    reset() {
      this.referenceNumber = null;
      this.report = {...this.report,
        description: '',
        //confirmEmail : true,
        images: [],
      };
    },
  },
};
</script>


<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
fieldset {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-bottom: 24px;
}
.input-field {
  margin-bottom: 15px;
}

#error-report-form {
  width: 100%;
  padding-bottom: 15px;
}


label {
  margin-bottom: 4px;
  display: inline-block;
  margin-top: 20px;
}

.mypages [class*=grid-] {
  padding-left: 0;
  padding-right: 0;
}


.btn-primary:disabled {
  cursor: default;
  color: $textcolorwhite;
  background-color: $gray;
}

input, textarea {
  display: block;
  width: 100%;
  max-width: 100%;
  padding: 1.6rem;
  background: #fff;
  line-height: 1.4;
  outline: none;
  transition: box-shadow .15s linear;

  &:not(:focus).is-danger {
    border-color: #D32F2F !important;
  }

  &:focus-within {
    outline: $abgblue 2px solid !important;
  }

  &:disabled {
    background-color: $lightgray;
  }

  &::placeholder {
    color: rgba(94, 94, 94, var(--tw-text-opacity, 1));
  }

  &:focus {
    box-shadow: unset !important;
    border: 1px solid $abgdarkblue !important;
  }

  &[type=checkbox] {
    width: 20px;
    height: 20px;
    box-shadow: none;
    accent-color: #007893;
  }

  &[type="radio"] {
    width: fit-content;
    display: block !important;
    accent-color: #007893;
    margin-right: 8px;

    &:checked+label {
      background: transparent !important;
    }
  }
}

[role="alert"] {
  color: #000!important;
  background-color: #FBEBEB;
  border: 1px solid #D32F2F;
  padding: 12px 16px;
  margin-top: 8px;
  display: inline-block;
  border-radius: 2px;
  font-size: 1.3rem!important;
}

label {
  font-weight: 600;
  font-size: 1.8rem;
  line-height: 1.2;
}

.dropdown {
  border-radius: 10px;
  border: 1px solid #E9E9E9;
  background: #FFF;
  position: absolute;
  z-index: 10;
  width: 100%;
  max-height: 200px;
  overflow: auto;
  box-shadow: 0 0 18px 0 rgba(0, 0, 0, 0.10);
}

.dropdown-item {
  display: flex;
  padding: 4px 8px;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-bottom: 1px solid #E9E9E9;
}
.button-container {
  display: flex;
  justify-content: flex-end;
}
</style>
<style lang="scss">
.search div:not([class]) {
  margin: 0 !important;

  & input {
    margin: 0 !important;
    padding-top: 16px !important;
    padding-bottom: 16px !important;

    &:focus-within {
      outline: $abgblue 2px solid !important;
    }
  }

  & button.start {
    top: 50%;
    transform: translateY(-50%);
    left: 12px;
    right: unset;
  }
}
</style>
